/**
 * useCreateInvitationLink Hook
 *
 * Hook for creating and managing invitation links
 */

import { useState, useCallback } from 'react';

import { useUser } from 'global/UserContext';
import { useToast } from 'hooks/useToast';
import { advisoryService } from 'services/advisoryService';
import { apiOrchestrator } from 'services/apiService';
import { CreateInvitationLinkDTO, InvitationLink } from 'types/leadRequestsTypes';

interface InviteeFormData {
  invitee_first_name: string;
  invitee_last_name: string;
  invitee_cell_phone: string;
  invitee_email: string;
  notify_user: boolean;
}

interface CreateInviteResponse {
  id: string;
  [key: string]: any;
}

interface UseCreateInvitationLinkReturn {
  createLink: (leadRequestId: string, formData: InviteeFormData) => Promise<InvitationLink | null>;
  loading: boolean;
  error: string | null;
  linkData: InvitationLink | null;
}

export const useCreateInvitationLink = (): UseCreateInvitationLinkReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linkData, setLinkData] = useState<InvitationLink | null>(null);
  const { toastError, toastSuccess } = useToast();
  const { user } = useUser();

  const createLink = useCallback(
    async (leadRequestId: string, formData: InviteeFormData): Promise<InvitationLink | null> => {
      setLoading(true);
      setError(null);

      try {
        // Validate user has person_id
        if (!user?.id) {
          const errorMsg = 'User person ID not found';
          setError(errorMsg);
          toastError(errorMsg);
          return null;
        }

        // Step 1: Create invite via orchestrator endpoint
        const notifyUserParam = formData.notify_user ? 'true' : 'false';
        const inviteUrl = `/invites/${user.id}?notify_user=${notifyUserParam}`;

        const inviteBody = {
          invitee_first_name: formData.invitee_first_name,
          invitee_last_name: formData.invitee_last_name,
          invitee_cell_phone: formData.invitee_cell_phone,
          invitee_email: formData.invitee_email,
          invite_status: 'pending',
          type: 'LEAD_REQUEST',
          extras: {
            lead_request_id: leadRequestId,
          },
        };

        const inviteResponse = await apiOrchestrator.post<CreateInviteResponse>(
          inviteUrl,
          inviteBody
        );

        const inviteId = inviteResponse.data.id;

        if (!inviteId) {
          throw new Error('Invite ID not found in response');
        }

        // Step 2: Generate unique link ID and create invitation link
        const linkId = inviteId;

        const createLinkData: CreateInvitationLinkDTO = {
          lead_request_id: leadRequestId,
          link: linkId,
        };

        const createdLink = await advisoryService.createInvitationLink(createLinkData);

        // Step 3: Update the link with the invite_id
        const updatedLink = await advisoryService.updateInvitationLink(createdLink.id, {
          invite_id: inviteId,
        });

        const response = updatedLink;

        setLinkData(response);
        toastSuccess('Link de convite criado com sucesso!');

        return response;
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || 'Falha ao criar link de convite';
        setError(errorMessage);
        toastError(errorMessage);
        console.error('Error creating invitation link:', err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [toastError, toastSuccess, user]
  );

  return {
    createLink,
    loading,
    error,
    linkData,
  };
};
